/// <reference types="vite/client" />

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role:
    | "ADMIN"
    | "PSICOLOGA"
    | "RECEPCAO"
    | "PACIENTE"
    | "FAMILIAR"
    | "EMPRESA";
}

export interface LoginResponse {
  accessToken: string;
  user: User;
}

export interface TwoFactorRequiredResponse {
  requires2FA: true;
  tempToken: string;
  message: string;
}

export interface AuthError {
  error: string;
  message: string;
  remainingAttempts?: number;
  blockedUntil?: string;
}

class AuthService {
  private accessToken: string | null = null;

  constructor() {
    // Try to restore token from localStorage
    this.accessToken = localStorage.getItem("accessToken");
  }

  setToken(token: string | null) {
    this.accessToken = token;
    if (token) {
      localStorage.setItem("accessToken", token);
    } else {
      localStorage.removeItem("accessToken");
    }
  }

  getToken(): string | null {
    return this.accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: globalThis.RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...((options.headers as Record<string, string>) || {}),
        },
        credentials: "include", // Include cookies for refresh token
      });

      const data = await response.json();

      if (!response.ok) {
        return { error: data.error || "Request failed", message: data.message };
      }

      return { data };
    } catch (error) {
      return {
        error: "Network Error",
        message:
          error instanceof Error
            ? error.message
            : "Erro de conexão com o servidor",
      };
    }
  }

  async login(
    email: string,
    password: string,
  ): Promise<ApiResponse<LoginResponse | TwoFactorRequiredResponse>> {
    const response = await this.request<
      LoginResponse | TwoFactorRequiredResponse
    >("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    // Only set token if it's a successful login (not 2FA required)
    if (response.data && "accessToken" in response.data) {
      this.setToken(response.data.accessToken);
    }

    return response;
  }

  async verify2FA(
    token: string,
    tempToken: string,
  ): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>("/api/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ token }),
      headers: {
        "X-2FA-Temp-Token": tempToken,
      },
    });

    if (response.data) {
      this.setToken(response.data.accessToken);
    }

    return response;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.request<void>("/api/auth/logout", {
      method: "POST",
    });

    this.setToken(null);
    return response;
  }

  async refreshToken(): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>("/api/auth/refresh", {
      method: "POST",
    });

    if (response.data) {
      this.setToken(response.data.accessToken);
    }

    return response;
  }

  async me(): Promise<ApiResponse<{ user: User }>> {
    return this.request<{ user: User }>("/api/auth/me");
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  /**
   * Set token directly (for OAuth callback / WebAuthn flows)
   */
  loginWithToken(token: string) {
    this.setToken(token);
  }
}

export const authService = new AuthService();
export default authService;
